"use strict";

/**
 * src/pages/analysis/telegramNotificationSettingsApi.js
 *
 * PHASE 2C.1 — Frontend Telegram Notification Settings API client.
 *
 * Thin, dependency-injectable HTTP client for the four
 * PHASE 1D.2C / 2B.3 backend endpoints:
 *
 *   GET  /api/analysis/settings/telegram-link/status
 *   POST /api/analysis/settings/telegram-link/start
 *   GET  /api/analysis/settings/telegram-notifications
 *   PUT  /api/analysis/settings/telegram-notifications
 *
 * This module is CommonJS (matches the existing CJS pattern used by
 * other src/pages/analysis/*.js modules, e.g. sharpArbAdapter.js) so
 * it can be require()'d directly by a Node self-check without any
 * build step, while still being consumable from the webpack/CRA
 * frontend bundle exactly like those other CJS modules already are.
 *
 * Dependency injection: `createTelegramNotificationSettingsApi({ auth,
 * fetchFn })` accepts a fake Firebase Web Auth object and/or a fake
 * fetch function, which lets telegramNotificationSettingsApiSelfcheck.js
 * exercise every branch below fully offline — no real Firebase call,
 * no real network call, ever.
 *
 * This module deliberately does NOT:
 *   - call auth.currentUser.getIdToken(...) or fetch(...) at
 *     require-time (both only ever happen inside a method call, and
 *     only after the caller's Firebase user has been validated),
 *   - construct a default client instance at require-time (the four
 *     directly-exported functions are lazy wrappers around a
 *     lazily-created singleton built from the default factory),
 *   - put a token / UID / chatId into a URL, request body, error
 *     object, return value, or console.* call,
 *   - log anything via console.log / console.info / console.warn /
 *     console.error / console.debug,
 *   - touch window / document / localStorage / navigator,
 *   - mutate its `options` argument, the injected `auth` / `fetchFn`,
 *     or any request payload passed in by the caller,
 *   - re-implement backend value validation (scope / minThreshold /
 *     bookmakers / markets semantics) — the backend remains the
 *     single source of truth for those rules; this client only
 *     enforces the wire-level shape contract described in the PHASE
 *     2C.1 spec.
 */

const firebaseAuthModule = require("../../utils/firebase");

// Read once, at require-time: this only reads the already-initialized
// `auth` reference off the firebase.js module (a plain property read,
// not a network/Firebase call). firebase.js itself is required by
// other existing frontend modules (e.g. adminUsersApi.js) the exact
// same way, so this does not introduce any new require-time behavior.
const DEFAULT_AUTH = firebaseAuthModule && typeof firebaseAuthModule === "object" ? firebaseAuthModule.auth : null;

// =========================================================================
// Constants
// =========================================================================

const TELEGRAM_LINK_STATUS_PATH = "/api/analysis/settings/telegram-link/status";
const TELEGRAM_LINK_START_PATH = "/api/analysis/settings/telegram-link/start";
const TELEGRAM_NOTIFICATION_SETTINGS_PATH = "/api/analysis/settings/telegram-notifications";

const TAB_NAMES = ["steam", "value", "arb", "middles"];
const VALID_SCOPES = new Set(["live", "early", "both"]);
const ALLOWED_UPDATE_INPUT_KEYS = new Set(["tab", "enabled", "scope", "minThreshold", "bookmakers", "markets"]);

// Accept-list for PUT-writable tabs — deliberately separate from
// TAB_NAMES (the four-tab GET contract): only "arb" and "value" may be
// written. Steam and middles must NEVER be accepted here.
const WRITABLE_TABS = new Set(["arb", "value"]);

// =========================================================================
// Generic helpers (no side effects)
// =========================================================================

function isPlainObject(value) {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  try {
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
  } catch (_e) {
    return false;
  }
}

function isNonEmptyString(value) {
  return typeof value === "string" && value.trim().length > 0;
}

function isNullOrStringArray(value) {
  if (value === null) return true;
  if (!Array.isArray(value)) return false;
  return value.every((item) => typeof item === "string");
}

/**
 * Strict shape/value validation for a single tab's settings object, as
 * defined by the PHASE 2C.1 spec's per-tab value contract.
 *
 * @param {*} value
 * @returns {boolean}
 */
function isValidTabSettings(value) {
  if (!isPlainObject(value)) return false;
  if (typeof value.available !== "boolean") return false;
  if (typeof value.enabled !== "boolean") return false;
  if (typeof value.scope !== "string" || !VALID_SCOPES.has(value.scope)) return false;
  const minThreshold = value.minThreshold;
  const isValidMinThreshold =
    minThreshold === null || (typeof minThreshold === "number" && Number.isFinite(minThreshold) && minThreshold >= 0);
  if (!isValidMinThreshold) return false;
  if (!isNullOrStringArray(value.bookmakers)) return false;
  if (!isNullOrStringArray(value.markets)) return false;
  return true;
}

/**
 * Rebuilds a tab settings object from ONLY the whitelisted fields, in
 * a fresh object literal — never returns/spreads the server's
 * original object. Caller MUST have already validated `value` via
 * isValidTabSettings(). Arrays are cloned (never shared references).
 *
 * @param {object} value
 */
function sanitizeTabSettings(value) {
  return {
    available: value.available,
    enabled: value.enabled,
    scope: value.scope,
    minThreshold: value.minThreshold,
    bookmakers: Array.isArray(value.bookmakers) ? value.bookmakers.slice() : null,
    markets: Array.isArray(value.markets) ? value.markets.slice() : null,
  };
}

// =========================================================================
// Error contract
// =========================================================================

/**
 * Public error shape: only `name`, `message`, `status`, `code`,
 * `field`, `tab` are ever set on instances of this class. Never
 * carries a token, Firebase user, response object, request
 * headers/body, server stack trace, UID, or chatId.
 */
class TelegramNotificationSettingsApiError extends Error {
  constructor({ status, code, message, field, tab } = {}) {
    super(isNonEmptyString(message) ? message : "Request failed");
    this.name = "TelegramNotificationSettingsApiError";
    this.status = typeof status === "number" && Number.isFinite(status) ? status : 0;
    this.code = isNonEmptyString(code) ? code : "request_failed";
    this.field = isNonEmptyString(field) ? field : null;
    this.tab = isNonEmptyString(tab) ? tab : null;
  }
}

function buildInvalidResponseError(httpStatus) {
  return new TelegramNotificationSettingsApiError({
    status: httpStatus,
    code: "invalid_response",
    message: "Received an invalid response from the server.",
  });
}

/**
 * Builds a TelegramNotificationSettingsApiError from a non-2xx HTTP
 * response, reading only the whitelisted fields (error/message/field/tab)
 * from the (already backend-sanitized) parsed JSON body, if any.
 *
 * @param {number} httpStatus
 * @param {*} parsedBody
 */
function buildApiErrorFromResponse(httpStatus, parsedBody) {
  const body = isPlainObject(parsedBody) ? parsedBody : {};
  const code = isNonEmptyString(body.error) ? body.error : "request_failed";
  const message = isNonEmptyString(body.message) ? body.message : "Request failed";
  const field = isNonEmptyString(body.field) ? body.field : null;
  const tab = isNonEmptyString(body.tab) ? body.tab : null;
  return new TelegramNotificationSettingsApiError({ status: httpStatus, code, message, field, tab });
}

// =========================================================================
// Default dependencies (resolved lazily at call-time, never at require-time)
// =========================================================================

/**
 * Default fetchFn: resolves the browser-global `fetch` at CALL-time
 * (every invocation looks it up fresh), never at require-time.
 */
function defaultFetchFn(...args) {
  const fetchImpl = typeof fetch === "function" ? fetch : null;
  if (typeof fetchImpl !== "function") {
    throw new Error("global fetch is not available in this environment");
  }
  return fetchImpl(...args);
}

// =========================================================================
// Authenticated request core
// =========================================================================

/**
 * @param {*} response - a fetch-like Response object (or fake)
 * @returns {Promise<*>} parsed JSON body, or null if missing/malformed
 */
async function safeParseJson(response) {
  if (!response || typeof response.json !== "function") {
    return null;
  }
  try {
    return await response.json();
  } catch (_e) {
    return null;
  }
}

/**
 * @param {*} parsedBody
 * @returns {boolean} true iff parsedBody looks like a token-related 401
 *   error, per the PHASE 2C.1 retry contract.
 */
function looksLikeTokenError(parsedBody) {
  if (!isPlainObject(parsedBody)) return false;
  const candidates = [parsedBody.error, parsedBody.message];
  return candidates.some((value) => typeof value === "string" && /token|invalid/i.test(value));
}

/**
 * Performs exactly one authenticated HTTP request, with at most one
 * automatic retry (using a force-refreshed ID token) when the first
 * response is a token-related 401, per the PHASE 2C.1 contract.
 *
 * @param {{auth:*, fetchFn:Function}} deps
 * @param {{method:string, path:string, body?:*}} requestSpec
 * @returns {Promise<{httpStatus:number, body:*}>}
 */
async function performAuthenticatedRequest(deps, requestSpec) {
  const { auth, fetchFn } = deps;
  const { method, path, body } = requestSpec;
  const hasBody = body !== undefined;

  const currentUser = auth && typeof auth === "object" ? auth.currentUser : null;
  if (!currentUser || typeof currentUser.getIdToken !== "function") {
    throw new TelegramNotificationSettingsApiError({
      status: 401,
      code: "firebase_auth_required",
      message: "Firebase authentication is required.",
    });
  }

  async function attempt(forceRefresh) {
    let idToken;
    try {
      idToken = await currentUser.getIdToken(forceRefresh);
    } catch (_e) {
      throw new TelegramNotificationSettingsApiError({
        status: 401,
        code: "firebase_auth_required",
        message: "Firebase authentication is required.",
      });
    }

    const headers = {
      Authorization: `Bearer ${idToken}`,
      Accept: "application/json",
    };
    if (hasBody) {
      headers["Content-Type"] = "application/json";
    }

    const init = { method, headers };
    if (hasBody) {
      init.body = JSON.stringify(body);
    }

    let response;
    try {
      response = await fetchFn(path, init);
    } catch (_e) {
      throw new TelegramNotificationSettingsApiError({
        status: 0,
        code: "network_error",
        message: "Network request failed.",
      });
    }

    if (!response || typeof response !== "object" || typeof response.ok !== "boolean") {
      throw new TelegramNotificationSettingsApiError({
        status: 0,
        code: "network_error",
        message: "Network request failed.",
      });
    }

    const parsedBody = await safeParseJson(response);
    return { response, parsedBody };
  }

  let { response, parsedBody } = await attempt(false);

  if (!response.ok && response.status === 401 && looksLikeTokenError(parsedBody)) {
    ({ response, parsedBody } = await attempt(true));
  }

  if (!response.ok) {
    throw buildApiErrorFromResponse(response.status, parsedBody);
  }

  return { httpStatus: response.status, body: parsedBody };
}

// =========================================================================
// Method implementations (operate on an already-resolved deps object)
// =========================================================================

async function getTelegramLinkStatusImpl(deps) {
  const { httpStatus, body } = await performAuthenticatedRequest(deps, {
    method: "GET",
    path: TELEGRAM_LINK_STATUS_PATH,
  });

  if (
    !isPlainObject(body) ||
    body.ok !== true ||
    (body.status !== "already_linked" && body.status !== "not_linked")
  ) {
    throw buildInvalidResponseError(httpStatus);
  }

  return { status: body.status };
}

async function startTelegramLinkImpl(deps) {
  const { httpStatus, body } = await performAuthenticatedRequest(deps, {
    method: "POST",
    path: TELEGRAM_LINK_START_PATH,
  });

  if (!isPlainObject(body) || body.ok !== true) {
    throw buildInvalidResponseError(httpStatus);
  }

  if (body.status === "already_linked") {
    return { status: "already_linked" };
  }

  if (body.status === "created") {
    const { deepLink, expiresInSec } = body;
    if (!isNonEmptyString(deepLink) || deepLink.indexOf("https://t.me/") !== 0) {
      throw buildInvalidResponseError(httpStatus);
    }
    if (typeof expiresInSec !== "number" || !Number.isFinite(expiresInSec) || expiresInSec <= 0) {
      throw buildInvalidResponseError(httpStatus);
    }
    return { status: "created", deepLink, expiresInSec };
  }

  throw buildInvalidResponseError(httpStatus);
}

async function getTelegramNotificationSettingsImpl(deps) {
  const { httpStatus, body } = await performAuthenticatedRequest(deps, {
    method: "GET",
    path: TELEGRAM_NOTIFICATION_SETTINGS_PATH,
  });

  if (!isPlainObject(body) || body.ok !== true || typeof body.linked !== "boolean") {
    throw buildInvalidResponseError(httpStatus);
  }

  const tabs = body.tabs;
  if (!isPlainObject(tabs)) {
    throw buildInvalidResponseError(httpStatus);
  }
  const tabKeys = Object.keys(tabs);
  if (tabKeys.length !== TAB_NAMES.length || !TAB_NAMES.every((name) => tabKeys.includes(name))) {
    throw buildInvalidResponseError(httpStatus);
  }

  const sanitizedTabs = {};
  for (const tabName of TAB_NAMES) {
    const tabValue = tabs[tabName];
    if (!isValidTabSettings(tabValue)) {
      throw buildInvalidResponseError(httpStatus);
    }
    sanitizedTabs[tabName] = sanitizeTabSettings(tabValue);
  }

  return { linked: body.linked, tabs: sanitizedTabs };
}

async function updateTelegramNotificationSettingsImpl(deps, input) {
  if (!isPlainObject(input)) {
    throw new TelegramNotificationSettingsApiError({
      status: 400,
      code: "invalid_client_input",
      message: "Input must be a plain object.",
    });
  }

  const inputKeys = Object.keys(input);
  for (const key of inputKeys) {
    if (!ALLOWED_UPDATE_INPUT_KEYS.has(key)) {
      throw new TelegramNotificationSettingsApiError({
        status: 400,
        code: "invalid_client_input",
        message: "Unsupported field in request.",
        field: key,
      });
    }
  }

  // "tab" is REQUIRED and must be an EXACT primitive string ("arb" or
  // "value") — no trim/lowercase/String(...) coercion, so missing/null/
  // boxed String/numeric/object/array/whitespace/case-near-miss values
  // are all rejected here, before ever touching auth or fetch.
  if (!WRITABLE_TABS.has(input.tab)) {
    throw new TelegramNotificationSettingsApiError({
      status: 400,
      code: "invalid_client_input",
      message: "Unsupported tab value in request.",
      field: "tab",
    });
  }

  // Clone the payload (and any array fields) before serializing — never
  // mutate the caller's `input` object or its arrays.
  const payload = {};
  for (const key of inputKeys) {
    const value = input[key];
    if (key === "bookmakers" || key === "markets") {
      payload[key] = Array.isArray(value) ? value.slice() : value;
    } else {
      payload[key] = value;
    }
  }

  const { httpStatus, body } = await performAuthenticatedRequest(deps, {
    method: "PUT",
    path: TELEGRAM_NOTIFICATION_SETTINGS_PATH,
    body: payload,
  });

  if (
    !isPlainObject(body) ||
    body.ok !== true ||
    body.status !== "updated" ||
    typeof body.tab !== "string" ||
    !WRITABLE_TABS.has(body.tab) ||
    !isValidTabSettings(body.settings)
  ) {
    throw buildInvalidResponseError(httpStatus);
  }

  return { status: "updated", tab: body.tab, settings: sanitizeTabSettings(body.settings) };
}

// =========================================================================
// Factory
// =========================================================================

/**
 * @param {{auth?:*, fetchFn?:Function}} [options]
 * @returns {Readonly<{
 *   getTelegramLinkStatus: () => Promise<{status:string}>,
 *   startTelegramLink: () => Promise<object>,
 *   getTelegramNotificationSettings: () => Promise<{linked:boolean, tabs:object}>,
 *   updateTelegramNotificationSettings: (input:object) => Promise<object>,
 * }>}
 */
function createTelegramNotificationSettingsApi(options) {
  if (options !== undefined && !isPlainObject(options)) {
    throw new TypeError("createTelegramNotificationSettingsApi: options must be a plain object when provided");
  }
  const opts = options === undefined ? {} : options;

  const hasAuthOverride = Object.prototype.hasOwnProperty.call(opts, "auth");
  const auth = hasAuthOverride ? opts.auth : DEFAULT_AUTH;
  if (hasAuthOverride && (!auth || typeof auth !== "object")) {
    throw new TypeError("createTelegramNotificationSettingsApi: options.auth must be an object when provided");
  }

  const hasFetchOverride = Object.prototype.hasOwnProperty.call(opts, "fetchFn");
  const fetchFn = hasFetchOverride ? opts.fetchFn : defaultFetchFn;
  if (hasFetchOverride && typeof fetchFn !== "function") {
    throw new TypeError("createTelegramNotificationSettingsApi: options.fetchFn must be a function when provided");
  }

  const deps = { auth, fetchFn };

  return Object.freeze({
    getTelegramLinkStatus: () => getTelegramLinkStatusImpl(deps),
    startTelegramLink: () => startTelegramLinkImpl(deps),
    getTelegramNotificationSettings: () => getTelegramNotificationSettingsImpl(deps),
    updateTelegramNotificationSettings: (input) => updateTelegramNotificationSettingsImpl(deps, input),
  });
}

// =========================================================================
// Lazy default-client wrappers (no default client / Firebase / fetch
// call ever happens at require-time — only on first actual call).
// =========================================================================

let defaultApiInstance = null;

function getDefaultApiInstance() {
  if (!defaultApiInstance) {
    defaultApiInstance = createTelegramNotificationSettingsApi();
  }
  return defaultApiInstance;
}

function getTelegramLinkStatus() {
  return getDefaultApiInstance().getTelegramLinkStatus();
}

function startTelegramLink() {
  return getDefaultApiInstance().startTelegramLink();
}

function getTelegramNotificationSettings() {
  return getDefaultApiInstance().getTelegramNotificationSettings();
}

function updateTelegramNotificationSettings(input) {
  return getDefaultApiInstance().updateTelegramNotificationSettings(input);
}

module.exports = {
  TelegramNotificationSettingsApiError,
  createTelegramNotificationSettingsApi,
  getTelegramLinkStatus,
  startTelegramLink,
  getTelegramNotificationSettings,
  updateTelegramNotificationSettings,
};
